import { useState, useRef } from 'react';
import type { AdSlot, InlineBanner } from '@/mocks/ads';

interface MobileAdCarouselProps {
  sideAds: AdSlot[];
  banners: InlineBanner[];
  className?: string;
}

export default function MobileAdCarousel({ sideAds, banners, className = '' }: MobileAdCarouselProps) {
  const allAds = [...sideAds.slice(0, 2), ...banners.slice(0, 1)];
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollTo = (idx: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const child = el.children[idx] as HTMLElement;
    if (child) {
      el.scrollTo({ left: child.offsetLeft - 16, behavior: 'smooth' });
    }
    setCurrentIndex(idx);
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const cardWidth = el.children[0]?.clientWidth || 260;
    const newIndex = Math.round(scrollLeft / (cardWidth + 16));
    setCurrentIndex(Math.max(0, Math.min(allAds.length - 1, newIndex)));
  };

  if (!allAds.length) return null;

  const ad = 'title' in allAds[currentIndex]
    ? { title: allAds[currentIndex].title, description: allAds[currentIndex].description, ctaUrl: allAds[currentIndex].ctaUrl, ctaText: allAds[currentIndex].ctaText, imageUrl: allAds[currentIndex].imageUrl }
    : { title: '', description: '', ctaUrl: '', ctaText: '', imageUrl: '' };

  return (
    <div className={`md:hidden ${className}`}>
      <div className="px-4">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-advertisement-line text-foreground-400 text-xs"></i>
          <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Publicidad</span>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-2"
        >
          {allAds.map((item, idx) => (
            <div
              key={idx}
              className="snap-center shrink-0 w-[260px] bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden cursor-pointer hover:border-primary-200 transition-all duration-300"
            >
              {'title' in item && (
                <>
                  <div className="h-32 overflow-hidden relative">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="inline-block px-2 py-0.5 bg-foreground-950/70 backdrop-blur-sm text-background-50 text-[10px] font-semibold rounded-md uppercase tracking-wider">
                        Publicidad
                      </span>
                    </div>
                  </div>
                  <div className="p-3">
                    <h4 className="text-foreground-950 font-semibold text-xs mb-1 line-clamp-2">{item.title}</h4>
                    <p className="text-foreground-500 text-[11px] leading-relaxed mb-2 line-clamp-2">{item.description}</p>
                    <a
                      href={item.ctaUrl}
                      className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-semibold text-[11px] transition-colors"
                    >
                      {item.ctaText}
                      <i className="ri-arrow-right-line text-[11px]"></i>
                    </a>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {allAds.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-3">
            {allAds.map((_, idx) => (
              <button
                key={idx}
                onClick={() => scrollTo(idx)}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  idx === currentIndex
                    ? 'bg-primary-500 w-4'
                    : 'bg-foreground-300 hover:bg-foreground-400'
                }`}
                aria-label={`Anuncio ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}